import { useCallback, useEffect, useState } from 'react';

// Components
import Alert from '../../../Alert';
import { useTranslation } from 'react-i18next';

// Queries
import getGrowingTogether from '../../../Queries/getGrowingTogether';

// Styles
import styles from '../index.module.sass';

// Images
import trophy from '../../../../images/consultancy/trofeo-bono.png';
import bonusCard from '../../../../images/consultancy/tarjeta-bono.png';

const isEmptyResponse = (response) => {
  if (response === null || response === undefined || response === '') {
    return true;
  }

  return (
    typeof response === 'object' &&
    !Array.isArray(response) &&
    Object.keys(response).length === 0
  );
};

const stageTwoLevels = Array.from({ length: 15 }, (_, index) => ({
  level: index + 5,
  qualification: `LE${index + 5}E`,
  serviceIndex: index + 1,
  stars: index,
}));

const getStageTwoStatus = (status) => {
  const normalizedStatus = String(status || '')
    .trim()
    .toUpperCase();

  if (normalizedStatus === 'BONO GANADO') {
    return 'earned';
  }

  if (normalizedStatus === 'PARTICIPANDO') {
    return 'participating';
  }

  return 'pending';
};

const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

const formatBonusDate = (date, language) => {
  if (!date) {
    return '';
  }

  const [year, month] = String(date).split('/');
  const monthNumber = Number(month);

  if (!year || monthNumber < 1 || monthNumber > 12) {
    return date;
  }

  const dateValue = new Date(
    Number(year),
    monthNumber - 1,
    1,
  );

  const locale = language.includes('es')
    ? 'es-MX'
    : 'en-US';

  const monthName = new Intl.DateTimeFormat(locale, {
    month: 'long',
  }).format(dateValue);

  return `${monthName} ${year}`;
};

export default function Stage2({
  getCookie,
  setSessionMessage,
}) {
  const { t, i18n } = useTranslation('growingtogether');
  const { language = '' } = i18n;

  const [open, setOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [loading, setLoading] = useState(true);
  const [bonusData, setBonusData] = useState(null);

const getStage2 = useCallback(async () => {
  setLoading(true);

  // Para pruebas del WS puede usarse temporalmente:
  // const kit = 5;

  // Kit real del usuario:
  const kit = getCookie('usuarioOk');

  const resp = await getGrowingTogether({
    tmbservice: 'GetBonoImpulsandoLideresEtapaDos',
    jsonBody: {
      kit,
    },
  });

  // Error técnico real del WS
  if (resp?.typeMessage === 'error') {
    const message =
      resp?.message ||
      t('queryError');

    setAlertText(message);
    setOpen(true);

    if (setSessionMessage) {
      setSessionMessage(message);
    }

    setBonusData(null);
    setLoading(false);
    return;
  }

  // 200 OK, pero sin información para este kit:
  // mostramos todo el camino como "Por participar"
  if (isEmptyResponse(resp)) {
    setBonusData({});
    setLoading(false);
    return;
  }

  // Etapa 2 sí devolvió información
  setBonusData(resp?.data ?? resp);
  setLoading(false);
}, [getCookie, setSessionMessage, t]);

  useEffect(() => {
    getStage2();
  }, [getStage2]);

  if (loading) {
    return (
      <div className={styles.stageMessage}>
        {t('loading')}
      </div>
    );
  }

  if (!bonusData) {
    return (
      <>
        <Alert
          open={open}
          setOpen={setOpen}
          title={t('attention')}
          text={alertText}
        />

        <div className={styles.stageMessage}>
          {t('noData')}
        </div>
      </>
    );
  }

  const bonuses = stageTwoLevels.map((bonus) => {
    const status = getStageTwoStatus(
      bonusData[`EstatusBono${bonus.serviceIndex}`],
    );

    return {
      ...bonus,
      status,
      amount:
        bonusData[`MontoBono${bonus.serviceIndex}`],
      date:
        bonusData[`Fecha${bonus.serviceIndex}`] || '',
    };
  });

  return (
    <section className={styles.stageTwo}>
      <Alert
        open={open}
        setOpen={setOpen}
        title={t('attention')}
        text={alertText}
      />

      <h2 className={styles.stageTwoSubtitle}>
        {t('stage2Range')}
      </h2>

      <div className={styles.stageTwoGrid}>
            {bonuses.map((bonus) => {
              const isEarned = bonus.status === 'earned';
              const isParticipating = bonus.status === 'participating';

              return (
                <article
                  key={bonus.level}
                  className={`${styles.stageTwoCard} ${
                    isEarned ? styles.stageTwoCardEarned : ''
                  } ${
                    isParticipating
                      ? styles.stageTwoCardParticipating
                      : ''
                  }`}
                >
              {isEarned && (
                <img
                  className={styles.stageTwoTrophy}
                  src={trophy}
                  alt={t('earnedBonus')}
                />
              )}

              <div className={styles.stageTwoInfo}>
                <p className={styles.stageTwoDescription}>
                  {bonus.stars === 0
                    ? t('qualificationTextInitial')
                    : bonus.stars === 1
                      ? t('qualificationTextOneStar')
                      : t('qualificationTextMultipleStars', {
                          stars: bonus.stars,
                        })}
                </p>

                {isEarned ? (
                  <>
                    <div
                      className={`${styles.stageTwoAmount} ${styles.stageTwoAmountEarned}`}
                    >
                      {formatCurrency(bonus.amount)}
                    </div>

                    {bonus.date && (
                      <p className={styles.stageTwoDate}>
                        {t('date')}: {formatBonusDate(bonus.date, language)}
                      </p>
                    )}
                  </>
                ) : (
                  <p
                    className={`${styles.stageTwoStatus} ${
                      isParticipating
                        ? styles.stageTwoStatusParticipating
                        : styles.stageTwoStatusPending
                    }`}
                  >
                    {t(bonus.status)}
                  </p>
                )}
              </div>

              <div className={styles.stageTwoCardVisual}>
                <img
                  src={bonusCard}
                  alt=''
                  className={styles.stageTwoCardImage}
                />
              </div>

            </article>
          );
        })}
      </div>
    </section>
  );
}